import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import fetcher from '../lib/fetcher';

import Error from '../components/Error';
import Layout from '../components/Layout/Layout';
import Spinner from '../components/Spinner';
import Table from '../components/Table/Table';

import { DocumentAction } from '../components/Documents/DocumentAction';
import { date } from '../lib/date';
import { isEmpty } from '../lib/isEmpty';

const TAB_KEYS = {
  sortenLizenzvertrag: 'sorten-lizenzvertrag',
  sonstigeVertraege: 'sonstige-vertraege',
  sonstigeRechnungen: 'sonstige-rechnungen-und-gutschriften',
  schadensmeldungen: 'schadensmeldungen',
  pflanzenschutzanschreiben: 'pflanzenschutzanschreiben',
  archiv: 'archiv',
};

const documentTabs = [
  {
    key: TAB_KEYS.sonstigeRechnungen,
    name: 'Sonstige Rechnungen und Gutschriften',
    href: `/wichtige-dokumente?tab=${TAB_KEYS.sonstigeRechnungen}`,
    type: 'invoices',
  },
  {
    key: TAB_KEYS.sonstigeVertraege,
    name: 'Sonstige Verträge',
    href: `/wichtige-dokumente?tab=${TAB_KEYS.sonstigeVertraege}`,
    type: 'messages',
    prefixes: ['LAV'],
  },
  {
    key: TAB_KEYS.schadensmeldungen,
    name: 'Schadensmeldungen',
    href: `/wichtige-dokumente?tab=${TAB_KEYS.schadensmeldungen}`,
    type: 'messages',
    prefixes: ['SMG'],
  },
  {
    key: TAB_KEYS.pflanzenschutzanschreiben,
    name: 'Pflanzenschutzanschreiben',
    href: `/wichtige-dokumente?tab=${TAB_KEYS.pflanzenschutzanschreiben}`,
    type: 'messages',
    prefixes: ['SMB'],
  },
  {
    key: TAB_KEYS.archiv,
    name: 'Archiv',
    href: `/wichtige-dokumente?tab=${TAB_KEYS.archiv}`,
    type: 'messages',
    prefixes: [],
    withoutPrefix: true,
  },
];

function sortByDateDesc(items, field) {
  return [...items].sort((a, b) => new Date(b[field]) - new Date(a[field]));
}

function getMessageData(messages, tab) {
  if (!Array.isArray(messages)) {
    return [];
  }

  if (tab.withoutPrefix) {
    return sortByDateDesc(
      messages.filter((message) => isEmpty(message.prefix)),
      'Datum',
    );
  }

  if (Array.isArray(tab.prefixes) && tab.prefixes.length > 0) {
    return sortByDateDesc(
      messages.filter((message) => tab.prefixes.includes(message.prefix)),
      'Datum',
    );
  }

  return sortByDateDesc(messages, 'Datum');
}

function getInvoiceData(invoices) {
  const items = Array.isArray(invoices?.data) ? invoices.data : [];
  return sortByDateDesc(items, 'Posting Date');
}

const DEFAULT_TAB_KEY = documentTabs[0].key;

const messageColumns = [
  {
    Header: 'Datum',
    accessor: 'Datum',
    Cell: (props) => date(props.value),
  },
  {
    Header: 'Betreff',
    accessor: 'Beschreibung',
    Cell: (props) => (props.row.original.prefix === 'SMG' ? 'Schadensmeldung ' + props.value : props.value),
  },
  {
    Header: 'Dokument öffnen',
    accessor: 'SourceCounter',
    Cell: (props) => <DocumentAction documents={[props.row.original]} />,
  },
];

const invoiceColumns = [
  {
    Header: 'Rechnungs- / Gutschriftnummer',
    accessor: 'No_',
    Cell: (props) => {
      if (/^\d/.test(props.value)) return 'Sonstige Rechnung/Gutschrift Nr. ' + props.value;
      return props.value;
    },
  },
  {
    Header: 'Datum',
    accessor: 'Posting Date',
    Cell: (props) => date(props.value),
  },
  {
    accessor: '0',
    Cell: (props) => <DocumentAction documents={[props.row.original.documents]} />,
  },
];

function DocumentTabs({ activeTab }) {
  return (
    <div className="overflow-x-auto pb-1 -mx-1 px-1">
      <span className="relative z-0 inline-flex min-w-max shadow-sm rounded-md">
        {documentTabs.map((tab, idx) => {
          const isActive = tab.key === activeTab.key;
          const positionClass = idx === 0 ? 'rounded-l-md' : idx === documentTabs.length - 1 ? 'rounded-r-md' : '';

          return (
            <Link
              key={tab.name}
              to={tab.href}
              className={`${positionClass} relative inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-secondary focus:border-secondary ${
                isActive ? 'bg-gray-50' : ''
              }`}
            >
              {tab.name}
            </Link>
          );
        })}
      </span>
    </div>
  );
}

export default function Documents() {
  const { data: messages, error: messagesError } = useSWR('/api/infos/messages', fetcher);
  const { data: invoices, error: invoicesError } = useSWR('/api/invoices/stock-and-interest', fetcher);
  const location = useLocation();

  const activeTabKey = new URLSearchParams(location.search).get('tab') || DEFAULT_TAB_KEY;
  const activeTab = documentTabs.find((tab) => tab.key === activeTabKey) || documentTabs[0];

  if (messagesError || invoicesError) return <Error error={messagesError || invoicesError} />;
  if (!messages || !invoices) return <Spinner />;

  const isInvoiceTab = activeTab.type === 'invoices';
  const tableData = isInvoiceTab ? getInvoiceData(invoices) : getMessageData(messages, activeTab);
  const tableColumns = isInvoiceTab ? invoiceColumns : messageColumns;

  return (
    <Layout>
      <Table
        title={activeTab.name}
        data={tableData}
        options={{ pdf: false, search: true }}
        columns={tableColumns}
        tabs={<DocumentTabs activeTab={activeTab} />}
      />
    </Layout>
  );
}
